import { Tooltip2 } from '@blueprintjs/popover2';
import { Icon } from 'antd';
import { getBookingTheme } from '../../components/roster-utilities';
import { IRosterBooking } from '../../types';
import { ROSTER_WEEKLY_CONFIG } from '../roster-weekly-config';
import { BookingTypeIcon, StatusTag } from 'common-components/tags';
import { Text } from 'common-components/typography';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { FaUserTimes } from 'react-icons/fa';
import { BookingStatus, BookingType, ServiceType, ShiftSlotStatus } from 'utilities/enum-utils';
import { Group, Paper, UnstyledButton, Avatar, ThemeIcon } from '@good/ui/core';
import { useBookings } from 'views/bookings-v2/books-view-context';
import { ROSTER_TAB } from '../../components/roster-common-enums';

interface BookingCardProps {
  booking: IRosterBooking;
}

const { ITEM_CARD_HEIGHT } = ROSTER_WEEKLY_CONFIG;

export function BookingCard({ booking }: BookingCardProps) {
  const { startDateTime, endDateTime, status, serviceName, scheduleName } = booking;
  const mStartDate = moment(startDateTime);
  const mEndDate = moment(endDateTime);
  const startDateDisplay = mStartDate.format('h:mm a');
  const endDateDisplay = mEndDate.format('h:mm a');
  const { bgColor, borderColor } = getBookingTheme(status);
  const onSelectRosterItem = useBookings((selector) => selector.onSelectRosterItem);
  const selectedRosterItem = useBookings((selector) => selector.selectedRosterItem);
  const isSelected = selectedRosterItem?.bookingId === booking.bookingId;

  const isCancelled = !!_.find(
    [
      BookingStatus.BUSINESS_CANCELLED,
      BookingStatus.CUSTOMER_CANCELLED,
      BookingStatus.CUSTOMER_CANCELLED_WITH_FEE,
      BookingStatus.CUSTOMER_CANCELLED_WITHOUT_FEE,
    ],
    (status) => status === booking.status,
  );

  const name = `${booking.workerFirstName?.[0]?.toUpperCase()} ${booking.workerLastName?.[0]?.toUpperCase()}`;

  return (
    <>
      <UnstyledButton
        onClick={() => onSelectRosterItem({ item: booking, type: 'customerBooking', tab: ROSTER_TAB.WEEKLY })}
        w='100%'
        h='100%'
        display='flex'
        style={{
          boxShadow: isSelected ? '0 0 0 2px var(--mantine-color-lavender-6)' : '',
          borderRadius: 'calc(var(--mantine-radius-md) - 2px)',
        }}
      >
        <Paper
          withBorder
          w='100%'
          shadow={isSelected ? 'xl' : 'xs'}
          p={8}
          mih={ITEM_CARD_HEIGHT}
          bg={bgColor}
          className='anim-fade-in'
          style={{ overflow: 'hidden', borderColor }}
        >
          <div className={'flex-row justify-between align-center '}>
            <StatusTag status={status} size='small' />
            {booking.serviceType === ServiceType.INDIVIDUAL && (
              <div className={'ml-small'}>
                <Tooltip2
                  content={
                    <Text color={'white'}>
                      {booking.workerId
                        ? `${booking.workerFirstName} ${booking.workerLastName}`
                        : booking.shiftSlotStatus === ShiftSlotStatus.PUBLISHING
                          ? 'Published'
                          : 'No team member assigned'}
                    </Text>
                  }
                  position={'bottom-right'}
                >
                  <Group gap={2} align='center'>
                    {booking.workerId ? (
                      <>
                        <Avatar size={20} name={name} color='initials' />
                        <ThemeIcon
                          variant='light'
                          radius='50%'
                          size={20}
                          color={
                            booking.shiftSlotStatus === ShiftSlotStatus.PENDING
                              ? 'var(--mantine-color-orange-4)'
                              : 'var(--mantine-color-ocean-3)'
                          }
                        >
                          <Icon
                            type={booking.shiftSlotStatus === ShiftSlotStatus.PENDING ? 'clock-circle' : 'check-circle'}
                          />
                        </ThemeIcon>
                      </>
                    ) : booking.shiftSlotStatus === ShiftSlotStatus.PUBLISHING ? (
                      <Icon type='wifi' className='text-color-violet-light mr-x-small text-size-regular' />
                    ) : !isCancelled ? (
                      <FaUserTimes className={'text-color-red text-size-large'} />
                    ) : (
                      <></>
                    )}
                  </Group>
                </Tooltip2>
              </div>
            )}
          </div>

          <div
            className='line-height-120 whitespace-nowrap overflow-hidden text-overflow-ellipsis'
            style={{ width: '100%' }}
          >
            <Text size='regular' lineHeight={120}>
              <b>{startDateDisplay}</b> - {endDateDisplay}
            </Text>
            <br />
            <div className={'flex-row align-center'}>
              <div style={{ minWidth: '16px' }}>
                <BookingTypeIcon
                  type={
                    booking.bookingType === BookingType.ACTIVITY_RECORD
                      ? BookingType.ACTIVITY_RECORD
                      : booking.serviceType
                  }
                  className={'mr-x-small'}
                  style={{ minWidth: '16px' }}
                />
              </div>
              <Text size='regular' lineHeight={120}>
                {booking.serviceType === ServiceType.GROUP && scheduleName ? scheduleName : serviceName}
              </Text>
            </div>
          </div>
        </Paper>
      </UnstyledButton>
    </>
  );
}
